import React, { useState, useEffect, useCallback } from "react";
import {
  useTranslate,
  useApiUrl,
  useNavigation,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import { Show } from "@pankod/refine-antd";
import {
  Table,
  Tag,
  Select,
  Space,
  Button,
  Typography,
  Descriptions,
  Modal,
  Tooltip,
  message,
} from "antd";
import { Progress } from "antd";
import { CheckCircleOutlined, TranslationOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  IVerificationJob,
  IVerificationItem,
  VerificationJobStatus,
  VerificationItemStatus,
} from "interfaces";

const { Paragraph } = Typography;

const STATUS_COLORS: Record<VerificationJobStatus, string> = {
  pending: "default",
  processing: "processing",
  completed: "success",
  partial: "warning",
  failed: "error",
};

const CONFIDENCE_COLORS: Record<string, string> = {
  high: "green",
  medium: "orange",
  low: "red",
};

const ITEM_STATUS_COLORS: Record<VerificationItemStatus, string> = {
  pending: "default",
  processing: "processing",
  verified: "success",
  failed: "error",
  crashed: "error",
};

export const VerificationJobShow: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");
  const { show } = useNavigation();
  const { id } = useParams<{ id: string }>();

  const [job, setJob] = useState<IVerificationJob | null>(null);
  const [items, setItems] = useState<IVerificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [agreesFilter, setAgreesFilter] = useState<string | undefined>("false");
  const [confidenceFilter, setConfidenceFilter] = useState<string | undefined>();

  // Translations modal
  const [translationsModal, setTranslationsModal] = useState<Record<string, string> | null>(null);

  const authHeaders = { Authorization: `Bearer ${APIToken}` };

  const fetchJob = useCallback(async () => {
    if (!id) return;
    setJobLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/verification-jobs/${id}`, {
        headers: authHeaders,
      });
      setJob(res.data);
    } catch {
      // ignore
    } finally {
      setJobLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apiUrl, APIToken]);

  const fetchItems = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const params: Record<string, any> = {
        _start: (page - 1) * pageSize,
        _end: page * pageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (agreesFilter !== undefined && agreesFilter !== "")
        params.agrees = agreesFilter;
      if (confidenceFilter) params.ai_confidence = confidenceFilter;

      const res = await axios.get(
        `${apiUrl}/verification-jobs/${id}/items`,
        { headers: authHeaders, params }
      );
      const rawItems: IVerificationItem[] = (res.data || []).map(
        (item: any) => ({
          ...item,
          quiz_answers: [
            item.quiz_answer1,
            item.quiz_answer2,
            item.quiz_answer3,
            item.quiz_answer4,
          ],
        })
      );
      setItems(rawItems);
      setTotal(parseInt(res.headers["x-total-count"] || "0", 10));
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apiUrl, APIToken, page, pageSize, statusFilter, agreesFilter, confidenceFilter]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Poll job while active
  useEffect(() => {
    if (!job || (job.status !== "pending" && job.status !== "processing"))
      return;
    const interval = setInterval(() => {
      fetchJob();
      fetchItems();
    }, 5000);
    return () => clearInterval(interval);
  }, [job, fetchJob, fetchItems]);

  const handleApply = () => {
    if (!job) return;
    Modal.confirm({
      title: t("verification.apply.confirm_title"),
      content: t("verification.apply.confirm_body", {
        total: job.disagreement_count,
        human: "—",
        ai: "—",
      }),
      onOk: async () => {
        try {
          const res = await axios.post(
            `${apiUrl}/verification-jobs/${id}/apply`,
            {},
            { headers: authHeaders }
          );
          message.success(
            t("verification.apply.success", {
              parents: res.data?.parents_updated ?? 0,
              children: res.data?.children_updated ?? 0,
            })
          );
          fetchJob();
          fetchItems();
        } catch (error) {
          const msg =
            axios.isAxiosError(error) && error.response?.data?.message
              ? error.response.data.message
              : "Failed to apply results";
          message.error(msg);
        }
      },
    });
  };

  const isApplied = !!job?.applied_at;
  const canApply =
    job &&
    (job.status === "completed" || job.status === "partial") &&
    !isApplied;

  const progressPercent =
    job && job.total_items > 0
      ? Math.round((job.processed_items / job.total_items) * 100)
      : 0;

  return (
    <Show
      isLoading={jobLoading}
      title={t("verification.items.title")}
      headerProps={{
        extra: (
          <Space>
            {canApply && (
              <Button type="primary" onClick={handleApply}>
                {t("verification.jobs.apply_results")}
              </Button>
            )}
            {isApplied && (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t("verification.apply.already_applied")}
              </Tag>
            )}
          </Space>
        ),
      }}
    >
      {job && (
        <Descriptions bordered size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label={t("verification.jobs.status")}>
            <Tag color={STATUS_COLORS[job.status]}>
              {t(`verification.jobs.status_${job.status}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.scope")}>
            <Space>
              <Tag>{job.scope}</Tag>
              {job.scope === "retry" && job.parent_job_id && (
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    show("verification-jobs", job.parent_job_id!)
                  }
                >
                  {t("verification.jobs.retried_from")}
                </Button>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.model")}>
            {job.model_name}{" "}
            <Tag>{job.model_provider}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.progress")}>
            <Space>
              <Progress
                percent={progressPercent}
                size="small"
                style={{ width: 120 }}
              />
              {job.processed_items}/{job.total_items}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.disagreements")}>
            {job.disagreement_count} ({job.disagreement_percent?.toFixed(1) ?? 0}%)
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.triggered_by")}>
            {job.triggered_by}
          </Descriptions.Item>
          <Descriptions.Item label={t("verification.jobs.languages")}>
            <Space size={[4, 4]} wrap>
              {job.languages?.length > 0
                ? job.languages.map((lang) => <Tag key={lang}>{lang}</Tag>)
                : "—"}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Filters */}
      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 160 }}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          allowClear
          placeholder={t("verification.items.all_statuses")}
        >
          <Select.Option value="verified">Verified</Select.Option>
          <Select.Option value="failed">Failed</Select.Option>
          <Select.Option value="crashed">Crashed</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          value={agreesFilter}
          onChange={(v) => {
            setAgreesFilter(v);
            setPage(1);
          }}
          allowClear
          placeholder={t("verification.items.all_agreements")}
        >
          <Select.Option value="false">
            {t("verification.items.disagreements_only")}
          </Select.Option>
          <Select.Option value="true">
            {t("verification.items.agreements_only")}
          </Select.Option>
        </Select>

        <Select
          style={{ width: 180 }}
          value={confidenceFilter}
          onChange={(v) => {
            setConfidenceFilter(v);
            setPage(1);
          }}
          allowClear
          placeholder={t("verification.items.all_confidences")}
        >
          <Select.Option value="high">
            {t("verification.items.confidence_high")}
          </Select.Option>
          <Select.Option value="medium">
            {t("verification.items.confidence_medium")}
          </Select.Option>
          <Select.Option value="low">
            {t("verification.items.confidence_low")}
          </Select.Option>
        </Select>
      </Space>

      {/* Items Table */}
      <Table
        dataSource={items}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1530 }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps || 50);
          },
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
        }}
      >
        <Table.Column
          dataIndex="quiz_question"
          title={t("verification.items.question")}
          width={250}
          render={(value: string, record: IVerificationItem) => (
            <Button
              type="link"
              style={{ padding: 0, whiteSpace: "normal", textAlign: "left", height: "auto" }}
              onClick={() => show("quiz", record.quiz_id)}
            >
              {value}
            </Button>
          )}
        />
        <Table.Column
          title={t("verification.items.answers")}
          width={300}
          render={(_, record: IVerificationItem) => (
            <div>
              {record.quiz_answers?.map((answer, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "2px 4px",
                    backgroundColor:
                      idx === record.current_answer - 1
                        ? "#e6f7ff"
                        : "transparent",
                    borderRadius: 2,
                    fontWeight:
                      idx === record.current_answer - 1 ? 600 : 400,
                  }}
                >
                  {idx + 1}. {answer}
                </div>
              ))}
            </div>
          )}
        />
        <Table.Column
          title={t("verification.items.original_answer")}
          width={120}
          render={(_, record: IVerificationItem) =>
            record.quiz_answers?.[record.original_answer - 1] ?? "—"
          }
        />
        <Table.Column
          title={t("verification.items.ai_answer")}
          width={200}
          render={(_, record: IVerificationItem) => {
            if (record.ai_answer === null || record.ai_answer === undefined)
              return "—";
            const text = record.quiz_answers?.[record.ai_answer - 1];
            const aiOutOfRange = !text;

            if (isApplied && !aiOutOfRange) {
              return (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {text}
                </Tag>
              );
            }

            if (aiOutOfRange) {
              return (
                <div>
                  <Tooltip
                    title={`${t("verification.items.ai_answer_not_available")}: ${record.ai_explanation?.match(/\[Correct answer: (.+?)\]/)?.[1] ?? `#${record.ai_answer}`}`}
                    overlayStyle={{ maxWidth: 400, wordBreak: "break-word" }}
                  >
                    <Tag color="orange" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t("verification.items.ai_answer_not_available")}:{" "}
                      {record.ai_explanation?.match(/\[Correct answer: (.+?)\]/)?.[1] ?? `#${record.ai_answer}`}
                    </Tag>
                  </Tooltip>
                  <br />
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => show("quiz", record.quiz_id)}
                  >
                    {t("verification.items.fix_manually")}
                  </Button>
                </div>
              );
            }

            if (record.ai_agrees === false) {
              return <Tag color="red">{text}</Tag>;
            }

            return <span>{text}</span>;
          }}
        />
        <Table.Column
          dataIndex="ai_confidence"
          title={t("verification.items.ai_confidence")}
          width={110}
          render={(value: string | null) =>
            value ? (
              <Tag color={CONFIDENCE_COLORS[value] ?? "default"}>
                {t(`verification.items.confidence_${value}`)}
              </Tag>
            ) : (
              "—"
            )
          }
        />
        <Table.Column
          title={t("verification.items.ai_explanation")}
          width={220}
          render={(_, record: IVerificationItem) => {
            const translations = record.ai_explanation_translations;
            const hasTranslations = translations && Object.keys(translations).length > 0;
            return (
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {record.ai_explanation ? (
                  <Paragraph
                    ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                    style={{ margin: 0 }}
                  >
                    {record.ai_explanation}
                  </Paragraph>
                ) : (
                  "—"
                )}
                {hasTranslations && (
                  <Button
                    type="link"
                    size="small"
                    icon={<TranslationOutlined />}
                    style={{ padding: 0 }}
                    onClick={() => setTranslationsModal(translations)}
                  >
                    {t("verification.items.translations")} ({Object.keys(translations).length})
                  </Button>
                )}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="status"
          title={t("verification.items.status")}
          width={100}
          render={(value: VerificationItemStatus) => (
            <Tag color={ITEM_STATUS_COLORS[value]}>{value}</Tag>
          )}
        />
      </Table>

      <Modal
        title={t("verification.items.translations")}
        visible={!!translationsModal}
        onCancel={() => setTranslationsModal(null)}
        footer={null}
        width={600}
      >
        {translationsModal && (
          <Descriptions bordered column={1} size="small">
            {Object.entries(translationsModal).map(([lang, text]) => (
              <Descriptions.Item key={lang} label={<Tag>{lang}</Tag>}>
                {text}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>
    </Show>
  );
};
